import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Account, AccountAssignments } from '../api';
import { Button, ErrorBanner, Spinner } from '../ui/primitives';
import { usePinGate } from '../auth/pinGate';

interface AssignmentRates {
  uplineRate: string;
  clientRate: string | null;
}

interface ManualRowSummary {
  accountId: number;
  pointsStated: string;
  clientPointsStated: string | null;
  rateStated: string;
}

interface GridRow {
  accountId: number;
  canonicalName: string;
  assignment: AssignmentRates | null;
  equalSpread: boolean;
  originalEqualSpread: boolean;
  uplinePoints: string;
  clientPoints: string;
}

interface Props {
  batchId: number;
  uplinePartyId: number;
  onSaved: (count: number) => void;
  onClose: () => void;
}

function calcAmt(pts: string, rate: string): string {
  const p = parseFloat(pts.replace(/,/g, ''));
  const r = parseFloat(rate);
  if (!Number.isFinite(p) || !Number.isFinite(r) || rate === '') return '';
  const val = p * r;
  return val.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Detects viewport < 768 px (below the Tailwind `md:` breakpoint). */
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return mobile;
}

export function ManualEntryGrid({ batchId, uplinePartyId, onSaved, onClose }: Props) {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  const accountsQ = useQuery<Account[]>({
    queryKey: ['accounts', 'upline', uplinePartyId],
    queryFn: () => api.get<Account[]>(`/api/accounts?upline=${uplinePartyId}&status=active`),
  });

  const ratesQ = useQuery<Map<number, AssignmentRates>>({
    queryKey: ['accounts', 'upline', uplinePartyId, 'rates'],
    enabled: (accountsQ.data?.length ?? 0) > 0,
    queryFn: async () => {
      const results = await Promise.allSettled(
        accountsQ.data!.map((a) =>
          api.get<AccountAssignments>(`/api/accounts/${a.id}/assignments`).then((r) => ({
            accountId: a.id,
            uplineRate: r.activeAssignment?.uplineRate ?? null,
            clientRate: r.activeAssignment?.clientRate ?? null,
          })),
        ),
      );
      const map = new Map<number, AssignmentRates>();
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value.uplineRate) {
          map.set(r.value.accountId, {
            uplineRate: r.value.uplineRate,
            clientRate: r.value.clientRate,
          });
        }
      }
      return map;
    },
  });

  const existingRowsQ = useQuery<{ rows: ManualRowSummary[] }>({
    queryKey: ['batch', batchId, 'manual-rows'],
    queryFn: () => api.get<{ rows: ManualRowSummary[] }>(`/api/batches/${batchId}/manual-rows`),
  });

  const [uplinePoints, setUplinePoints] = useState<Record<number, string>>({});
  const [clientPoints, setClientPoints] = useState<Record<number, string>>({});
  const [spreads, setSpreads] = useState<Record<number, boolean>>({});
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [prePopulated, setPrePopulated] = useState(false);

  const uplineRefs = useRef<(HTMLInputElement | null)[]>([]);
  const clientRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (prePopulated) return;
    if (!accountsQ.data || !existingRowsQ.data) return;

    const existingByAccountId = new Map(
      existingRowsQ.data.rows.map((r) => [r.accountId, r]),
    );

    const newUpline: Record<number, string> = {};
    const newClient: Record<number, string> = {};
    const newSelected: Record<number, boolean> = {};

    for (const acct of accountsQ.data) {
      const saved = existingByAccountId.get(acct.id);
      if (saved) {
        newUpline[acct.id] = saved.pointsStated;
        newClient[acct.id] = saved.clientPointsStated ?? '';
        newSelected[acct.id] = true;
      } else {
        newSelected[acct.id] = false;
      }
    }

    setUplinePoints(newUpline);
    setClientPoints(newClient);
    setSelected(newSelected);
    setPrePopulated(true);
  }, [accountsQ.data, existingRowsQ.data, prePopulated]);

  const rows: GridRow[] = (accountsQ.data ?? []).map((a) => {
    const assignment = ratesQ.data?.get(a.id) ?? null;
    const originalEqualSpread = a.equalSpread;
    return {
      accountId: a.id,
      canonicalName: a.canonicalName,
      assignment,
      equalSpread: spreads[a.id] !== undefined ? spreads[a.id]! : originalEqualSpread,
      originalEqualSpread,
      uplinePoints: uplinePoints[a.id] ?? '',
      clientPoints: clientPoints[a.id] ?? '',
    };
  });

  const rowsToSave = rows.filter(
    (r) => r.assignment !== null && selected[r.accountId] === true,
  );
  const spreadChanges = rows.filter((r) => r.equalSpread !== r.originalEqualSpread);
  const canSave = rowsToSave.length > 0 || spreadChanges.length > 0;

  const selectedCount = rows.filter((r) => selected[r.accountId] === true).length;
  const allSelected = rows.length > 0 && rows.every((r) => selected[r.accountId] === true);
  const someSelected = rows.some((r) => selected[r.accountId] === true);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (spreadChanges.length > 0) {
        await Promise.all(
          spreadChanges.map((r) =>
            api.patch(`/api/accounts/${r.accountId}/equal-spread`, { equalSpread: r.equalSpread }),
          ),
        );
      }
      if (rowsToSave.length > 0) {
        await api.post(`/api/batches/${batchId}/manual-rows`, {
          rows: rowsToSave.map((r) => ({
            accountId: r.accountId,
            pointsStated: r.uplinePoints.trim() || '0',
            rateStated: r.assignment!.uplineRate,
            clientPointsStated: !r.equalSpread
              ? (r.clientPoints.trim() || '0')
              : null,
          })),
        });
      }
      return rowsToSave.length;
    },
    onSuccess: (count) => {
      void qc.invalidateQueries({ queryKey: ['batch', batchId] });
      void qc.invalidateQueries({ queryKey: ['accounts', 'upline', uplinePartyId] });
      onSaved(count);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const focusableIdxs = rows.reduce<number[]>((acc, r, i) => {
    if (r.assignment !== null) acc.push(i);
    return acc;
  }, []);

  function toggleAll() {
    const next = !allSelected;
    const update: Record<number, boolean> = {};
    for (const r of rows) update[r.accountId] = next;
    setSelected((p) => ({ ...p, ...update }));
  }

  const isLoading = accountsQ.isLoading || existingRowsQ.isLoading;

  return (
    <div className="flex flex-col gap-4">
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-caption text-ink-muted">
          <Spinner /> Loading accounts…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-caption text-ink-muted">No active accounts found for this upline.</p>
      ) : isMobile ? (
        <ManualEntryStepperMobile
          rows={rows}
          ratesLoading={ratesQ.isLoading}
          uplinePoints={uplinePoints}
          setUplinePoints={setUplinePoints}
          clientPoints={clientPoints}
          setClientPoints={setClientPoints}
          spreads={spreads}
          setSpreads={setSpreads}
          selected={selected}
          setSelected={setSelected}
          canSave={canSave}
          onSave={() => void withPin(() => saveMut.mutateAsync())}
          isSaving={saveMut.isPending}
          onCancel={onClose}
          error={error}
        />
      ) : (
        <>
          <p className="text-caption text-ink-muted">
            Check the rows you want to update and enter Points P&amp;L. Unchecked rows are left
            untouched. Toggle <span className="font-medium text-ink">Same</span> to share
            one points figure across upline and client, or uncheck to enter separately.
          </p>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-caption">
              <colgroup>
                <col className="w-[36px]" />
                <col />
                <col className="w-[120px]" />
                <col className="w-[60px]" />
                <col className="w-[220px]" />
                <col className="w-[160px]" />
              </colgroup>
              <thead className="bg-surface-raised/60">
                <tr>
                  <th className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-border bg-surface accent-brand"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={toggleAll}
                      title={allSelected ? 'Deselect all' : 'Select all'}
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-ink-muted">Account</th>
                  <th className="px-3 py-2 text-left font-medium text-ink-muted">Rate</th>
                  <th className="px-3 py-2 text-center font-medium text-ink-muted">Same</th>
                  <th className="px-3 py-2 text-right font-medium text-ink-muted">Points P&amp;L</th>
                  <th className="px-3 py-2 text-right font-medium text-ink-muted">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {rows.map((row, idx) => {
                  const isSelected = selected[row.accountId] === true;
                  const focusPos = focusableIdxs.indexOf(idx);
                  const uPts = row.uplinePoints.trim();
                  const cPts = row.equalSpread ? uPts : row.clientPoints.trim();
                  const uRate = row.assignment?.uplineRate ?? '';
                  const cRate = row.assignment?.clientRate ?? '';
                  const uAmt = uPts && uRate ? calcAmt(uPts, uRate) : '';
                  const cAmt = cPts && cRate ? calcAmt(cPts, cRate) : '';
                  const spreadChanged = row.equalSpread !== row.originalEqualSpread;

                  return (
                    <tr
                      key={row.accountId}
                      className={`transition-colors hover:bg-surface-raised/40 ${
                        isSelected ? '' : 'opacity-40'
                      }`}
                    >
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-border bg-surface accent-brand"
                          checked={isSelected}
                          onChange={(e) =>
                            setSelected((p) => ({ ...p, [row.accountId]: e.target.checked }))
                          }
                        />
                      </td>

                      <td className="px-3 py-2 font-medium text-ink">
                        {row.canonicalName}
                      </td>

                      <td className="px-3 py-2">
                        {ratesQ.isLoading ? (
                          <span className="text-ink-muted">…</span>
                        ) : row.assignment === null ? (
                          <span className="text-caption text-warn">no assignment</span>
                        ) : row.equalSpread ? (
                          <span className="font-mono text-ink-muted">{row.assignment.uplineRate}</span>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1">
                              <span className="w-3 text-[10px] font-medium text-brand">U</span>
                              <span className="font-mono text-ink-muted">{row.assignment.uplineRate}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="w-3 text-[10px] font-medium text-pos">C</span>
                              <span className="font-mono text-ink-muted">{row.assignment.clientRate ?? '—'}</span>
                            </div>
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2 text-center">
                        <label className="inline-flex cursor-pointer flex-col items-center gap-0.5">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-border bg-surface accent-brand"
                            checked={row.equalSpread}
                            onChange={(e) =>
                              setSpreads((p) => ({ ...p, [row.accountId]: e.target.checked }))
                            }
                          />
                          {spreadChanged && (
                            <span className="text-[9px] leading-none text-brand">*</span>
                          )}
                        </label>
                      </td>

                      <td className="px-3 py-2">
                        {row.assignment === null ? (
                          <span className="text-caption text-ink-muted">—</span>
                        ) : row.equalSpread ? (
                          <input
                            ref={(el) => { uplineRefs.current[idx] = el; }}
                            type="text"
                            inputMode="decimal"
                            value={row.uplinePoints}
                            placeholder="0.00"
                            disabled={!isSelected}
                            className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-right font-mono text-small text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
                            onChange={(e) =>
                              setUplinePoints((p) => ({ ...p, [row.accountId]: e.target.value }))
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Tab' && !e.shiftKey && focusPos !== -1) {
                                e.preventDefault();
                                const next = focusableIdxs[focusPos + 1];
                                if (next !== undefined) uplineRefs.current[next]?.focus();
                              }
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const next = focusableIdxs[focusPos + 1];
                                if (next !== undefined) {
                                  uplineRefs.current[next]?.focus();
                                } else if (canSave) {
                                  void withPin(() => saveMut.mutateAsync());
                                }
                              }
                            }}
                          />
                        ) : (
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              <span className="w-4 shrink-0 text-[10px] font-medium text-brand">U</span>
                              <input
                                ref={(el) => { uplineRefs.current[idx] = el; }}
                                type="text"
                                inputMode="decimal"
                                value={row.uplinePoints}
                                placeholder="0.00"
                                disabled={!isSelected}
                                className="w-full rounded-md border border-border bg-surface px-2 py-1 text-right font-mono text-small text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                onChange={(e) =>
                                  setUplinePoints((p) => ({ ...p, [row.accountId]: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
                                    e.preventDefault();
                                    clientRefs.current[idx]?.focus();
                                  }
                                }}
                              />
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="w-4 shrink-0 text-[10px] font-medium text-pos">C</span>
                              <input
                                ref={(el) => { clientRefs.current[idx] = el; }}
                                type="text"
                                inputMode="decimal"
                                value={row.clientPoints}
                                placeholder="0.00"
                                disabled={!isSelected}
                                className="w-full rounded-md border border-border bg-surface px-2 py-1 text-right font-mono text-small text-ink placeholder:text-ink-muted focus:border-pos focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                onChange={(e) =>
                                  setClientPoints((p) => ({ ...p, [row.accountId]: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === 'Tab' && !e.shiftKey && focusPos !== -1) {
                                    e.preventDefault();
                                    const next = focusableIdxs[focusPos + 1];
                                    if (next !== undefined) uplineRefs.current[next]?.focus();
                                  }
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const next = focusableIdxs[focusPos + 1];
                                    if (next !== undefined) {
                                      uplineRefs.current[next]?.focus();
                                    } else if (canSave) {
                                      void withPin(() => saveMut.mutateAsync());
                                    }
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        {row.equalSpread ? (
                          <p className="text-right font-mono text-small text-ink">
                            {uAmt !== '' ? uAmt : <span className="text-ink-muted">—</span>}
                          </p>
                        ) : (
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-medium text-brand">U</span>
                              <span className="font-mono text-caption text-ink-muted">
                                {uAmt !== '' ? uAmt : '—'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] font-medium text-pos">C</span>
                              <span className="font-mono text-small text-ink">
                                {cAmt !== '' ? cAmt : '—'}
                              </span>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {spreadChanges.length > 0 && (
            <p className="text-caption text-brand">
              * {spreadChanges.length} account(s) have unsaved rate-type changes — will be saved with rows.
            </p>
          )}

          {error && <ErrorBanner>{error}</ErrorBanner>}

          <div className="flex items-center justify-between gap-4">
            <span className="text-caption text-ink-muted">
              {rows.length > 0
                ? `${selectedCount} of ${rows.length} row(s) selected — blank entries default to 0`
                : ''}
              {spreadChanges.length > 0 && selectedCount > 0 && ' · '}
              {spreadChanges.length > 0 &&
                `${spreadChanges.length} rate-type preference(s) will update`}
            </span>
            <div className="flex shrink-0 gap-2">
              <Button variant="ghost" onClick={onClose} disabled={saveMut.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => void withPin(() => saveMut.mutateAsync())}
                disabled={saveMut.isPending || !canSave || isLoading}
              >
                {saveMut.isPending ? (
                  <Spinner />
                ) : rowsToSave.length > 0 ? (
                  `Save ${rowsToSave.length} row(s)`
                ) : (
                  'Save preferences'
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface StepperMobileProps {
  rows: GridRow[];
  ratesLoading: boolean;
  uplinePoints: Record<number, string>;
  setUplinePoints: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  clientPoints: Record<number, string>;
  setClientPoints: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  spreads: Record<number, boolean>;
  setSpreads: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  selected: Record<number, boolean>;
  setSelected: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  canSave: boolean;
  onSave: () => void;
  isSaving: boolean;
  onCancel: () => void;
  error: string | null;
}

function ManualEntryStepperMobile({
  rows,
  ratesLoading,
  uplinePoints,
  setUplinePoints,
  clientPoints,
  setClientPoints,
  spreads,
  setSpreads,
  selected,
  setSelected,
  canSave,
  onSave,
  isSaving,
  onCancel,
  error,
}: StepperMobileProps) {
  const [step, setStep] = useState(0);
  const uInputRef = useRef<HTMLInputElement | null>(null);
  const cInputRef = useRef<HTMLInputElement | null>(null);
  const saveRef = useRef<HTMLButtonElement | null>(null);

  const total = rows.length;
  const clampedStep = Math.min(step, Math.max(0, total - 1));
  const row = rows[clampedStep];

  const isFirst = clampedStep === 0;
  const isLast = clampedStep === total - 1;

  const goNext = () => { if (!isLast) setStep((s) => s + 1); };
  const goPrev = () => { if (!isFirst) setStep((s) => s - 1); };

  useEffect(() => {
    const t = setTimeout(() => { uInputRef.current?.focus(); }, 60);
    return () => clearTimeout(t);
  }, [clampedStep]);

  const handleEnterLastInput = () => {
    if (!isLast) goNext();
    else saveRef.current?.focus();
  };

  if (!row) return null;

  const { accountId } = row;
  const isSelected = selected[accountId] === true;
  const uPts = (uplinePoints[accountId] ?? '').trim();
  const cPts = row.equalSpread ? uPts : (clientPoints[accountId] ?? '').trim();
  const uRate = row.assignment?.uplineRate ?? '';
  const cRate = row.assignment?.clientRate ?? '';
  const uAmt = uPts && uRate ? calcAmt(uPts, uRate) : '';
  const cAmt = cPts && cRate ? calcAmt(cPts, cRate) : '';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-body font-bold text-ink leading-tight">{row.canonicalName}</h4>
        <span className="shrink-0 text-small text-ink-muted">{clampedStep + 1} / {total}</span>
      </div>

      <div
        className={`rounded-xl border p-4 space-y-4 transition-opacity ${
          isSelected ? 'border-brand/30 bg-surface' : 'border-border/50 bg-surface-raised/20'
        }`}
      >
        <label className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3">
          <span className="text-small font-medium text-ink">Include this account</span>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => setSelected((p) => ({ ...p, [accountId]: e.target.checked }))}
            className="h-5 w-5 rounded border-border bg-surface accent-brand"
          />
        </label>

        {ratesLoading ? (
          <p className="text-caption text-ink-muted">Loading rates…</p>
        ) : row.assignment === null ? (
          <p className="text-small text-warn">No rate assignment — cannot enter points.</p>
        ) : (
          <>
            <div className="text-caption text-ink-muted">
              {row.equalSpread ? (
                <span>
                  Rate:{' '}
                  <span className="font-mono text-ink">{row.assignment.uplineRate}</span>
                </span>
              ) : (
                <div className="flex gap-5">
                  <span>
                    <span className="font-medium text-brand">U</span>{' '}
                    <span className="font-mono text-ink">{row.assignment.uplineRate}</span>
                  </span>
                  <span>
                    <span className="font-medium text-pos">C</span>{' '}
                    <span className="font-mono text-ink">
                      {row.assignment.clientRate ?? '—'}
                    </span>
                  </span>
                </div>
              )}
            </div>

            <label className="flex min-h-[44px] cursor-pointer items-center justify-between gap-3">
              <span className="text-small text-ink-muted">
                Same points for U &amp; C
                {row.equalSpread !== row.originalEqualSpread && (
                  <span className="ml-1 text-caption text-brand">*changed</span>
                )}
              </span>
              <input
                type="checkbox"
                checked={row.equalSpread}
                onChange={(e) =>
                  setSpreads((p) => ({ ...p, [accountId]: e.target.checked }))
                }
                className="h-5 w-5 rounded border-border bg-surface accent-brand"
              />
            </label>

            {row.equalSpread ? (
              <div className="space-y-1.5">
                <label className="text-caption font-medium text-ink-muted">Points P&amp;L</label>
                <input
                  ref={uInputRef}
                  type="text"
                  inputMode="decimal"
                  value={uplinePoints[accountId] ?? ''}
                  placeholder="0.00"
                  disabled={!isSelected}
                  className="w-full min-h-[48px] rounded-xl border border-border bg-surface px-4 py-3 text-base font-mono text-right text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:opacity-50"
                  onChange={(e) =>
                    setUplinePoints((p) => ({ ...p, [accountId]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleEnterLastInput(); }
                  }}
                />
                {uAmt !== '' && (
                  <p className="text-right text-small font-mono text-ink-muted">{uAmt}</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-caption font-medium text-brand">U — Upline Points</label>
                  <input
                    ref={uInputRef}
                    type="text"
                    inputMode="decimal"
                    value={uplinePoints[accountId] ?? ''}
                    placeholder="0.00"
                    disabled={!isSelected}
                    className="w-full min-h-[48px] rounded-xl border border-border bg-surface px-4 py-3 text-base font-mono text-right text-ink placeholder:text-ink-muted focus:border-brand focus:outline-none disabled:opacity-50"
                    onChange={(e) =>
                      setUplinePoints((p) => ({ ...p, [accountId]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
                        e.preventDefault();
                        cInputRef.current?.focus();
                      }
                    }}
                  />
                  {uAmt !== '' && (
                    <p className="text-right text-caption font-mono text-brand">{uAmt}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <label className="text-caption font-medium text-pos">C — Client Points</label>
                  <input
                    ref={cInputRef}
                    type="text"
                    inputMode="decimal"
                    value={clientPoints[accountId] ?? ''}
                    placeholder="0.00"
                    disabled={!isSelected}
                    className="w-full min-h-[48px] rounded-xl border border-border bg-surface px-4 py-3 text-base font-mono text-right text-ink placeholder:text-ink-muted focus:border-pos focus:outline-none disabled:opacity-50"
                    onChange={(e) =>
                      setClientPoints((p) => ({ ...p, [accountId]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); handleEnterLastInput(); }
                    }}
                  />
                  {cAmt !== '' && (
                    <p className="text-right text-caption font-mono text-pos">{cAmt}</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button variant="ghost" size="sm" disabled={isFirst} onClick={goPrev} className="w-full">
          ← Prev
        </Button>
        <Button variant="ghost" size="sm" disabled={isLast} onClick={goNext} className="w-full">
          Next →
        </Button>
      </div>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <Button
        ref={saveRef}
        onClick={onSave}
        disabled={isSaving || !canSave}
        className="w-full"
      >
        {isSaving ? <Spinner /> : 'Save all accounts'}
      </Button>
      <Button variant="ghost" onClick={onCancel} disabled={isSaving} className="w-full">
        Cancel
      </Button>
    </div>
  );
}
